import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatWeight,
  type CurrentMax,
  type Exercise,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { Card } from "../primitives/Card";
import { LoadingIndicator } from "../primitives/LoadingIndicator";
import { useDisclosure } from "../primitives/useDisclosure";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { weightUnitLabels } from "../weightUnitLabels";

// The coach's read-only MAX census. Split out of AthleteDetail (CO-7) to make
// room for the targets panel; behaviour unchanged.
//
// Read-only ON PURPOSE: the edit surface (MaxesView) is deliberately not mounted
// on the coach console — coach writes are CO-7's targets route and nothing else.
//
// The heaviest lifts lead and the long tail folds behind the same fold-control
// idiom as MuscleCoverageCompactSection, so a 50-MAX athlete can't stretch its grid row
// or bury the rhythm card.
const MAXES_COLLAPSED_COUNT = 10;

export function CoachMaxesCard({
  theme,
  maxes,
  catalog,
  units,
}: {
  theme: UiTheme;
  /** null while loading — a spinner, never an empty list. */
  maxes: Map<string, CurrentMax> | null;
  catalog: ReadonlyArray<Exercise> | null;
  /** The ATHLETE's unit preference, not the coach's. */
  units: UnitsPreference;
}) {
  const { t, i18n } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const styles = makeStyles(theme);
  const getName = useExerciseNameResolver(catalog);
  const fold = useDisclosure(false);

  // Same per-locale date idiom as the E1RM panel's point labels.
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [i18n.language],
  );

  const maxRows = useMemo(() => {
    if (!maxes) return null;
    return [...maxes.entries()]
      .map(([slug, m]) => ({ slug, ...m }))
      .sort((a, b) => b.valueKg - a.valueKg);
  }, [maxes]);

  const shownMaxRows =
    maxRows && !fold.expanded ? maxRows.slice(0, MAXES_COLLAPSED_COUNT) : maxRows;
  const hiddenMaxCount = (maxRows?.length ?? 0) - (shownMaxRows?.length ?? 0);

  return (
    <Card theme={theme}>
      <Text style={styles.cardTitle} accessibilityRole="header">
        {t("coach.detail.maxesTitle")}
      </Text>
      {shownMaxRows === null ? (
        <LoadingIndicator theme={theme} />
      ) : shownMaxRows.length === 0 ? (
        <Text style={styles.cardBody}>{t("coach.detail.maxesEmpty")}</Text>
      ) : (
        <>
          {shownMaxRows.map((m) => (
            <View key={m.slug} style={styles.maxRow}>
              <Text style={styles.maxName} numberOfLines={1}>
                {getName(m.slug)}
              </Text>
              <Text style={styles.maxDate}>{dateFmt.format(new Date(m.setAt))}</Text>
              <Text style={styles.maxValue}>
                {formatWeight(m.valueKg, units, i18n.language, unitLabels)}
              </Text>
            </View>
          ))}
          {hiddenMaxCount > 0 || fold.expanded ? (
            <Pressable
              onPress={fold.toggle}
              {...fold.a11yProps}
              hitSlop={8}
              accessibilityRole="button"
              style={styles.seeAll}
            >
              <Text style={styles.seeAllTxt}>
                {fold.expanded
                  ? t("coach.detail.maxesShowFewer")
                  : t("coach.detail.maxesSeeAll", { count: hiddenMaxCount })}
              </Text>
            </Pressable>
          ) : null}
        </>
      )}
    </Card>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    cardTitle: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      marginBottom: s.s2,
    },
    cardBody: { color: p.ink2, fontSize: ty.sizes.sm },
    maxRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      paddingVertical: s.s2,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: p.border,
    },
    // Row idiom mirrors Maxes/maxesStyles (name 500 / value 700 tabular, ink
    // not brand) at card-inset padding — the full-bleed list styles there
    // assume a pressable screen-wide row this static card doesn't have.
    maxName: {
      flex: 1,
      minWidth: 0,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "500",
    },
    maxDate: { color: p.ink3, fontSize: ty.sizes.xs },
    maxValue: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    // Same fold-control idiom as MuscleCoverageCompactSection.seeAll.
    seeAll: { paddingVertical: s.s2, minHeight: 44, justifyContent: "center" },
    seeAllTxt: { color: p.brandText, fontSize: ty.sizes.base, fontWeight: "600" },
  });
}
